import React from 'react';
import { Grid, Paper, Typography, Box, Stack, Tooltip, ButtonBase } from '@mui/material';
import { motion } from 'framer-motion';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarHalfRoundedIcon from '@mui/icons-material/StarHalfRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { feedbackHeroTokens } from './feedbackHeroTokens';

const StarFill = ({ rating, size = 16, color }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (rating >= i - 0.25) {
      stars.push(
        <StarRoundedIcon key={i} sx={{ fontSize: size, color }} />
      );
    } else if (rating >= i - 0.75) {
      stars.push(
        <StarHalfRoundedIcon key={i} sx={{ fontSize: size, color }} />
      );
    } else {
      stars.push(
        <StarBorderRoundedIcon
          key={i}
          sx={{ fontSize: size, color: 'rgba(15, 23, 42, 0.18)' }}
        />
      );
    }
  }
  return (
    <Stack direction="row" spacing={0} alignItems="center">
      {stars}
    </Stack>
  );
};

const MiniDistributionBar = ({ pos, neu, neg, colors, fonts }) => {
  const total = pos + neu + neg;
  if (total === 0) return null;
  const posPct = (pos / total) * 100;
  const neuPct = (neu / total) * 100;
  const negPct = (neg / total) * 100;

  const tooltipTitle = (
    <Box>
      <Typography sx={{ fontSize: '0.74rem' }}>
        <b>{pos}</b> positivos · <b>{neu}</b> neutros · <b>{neg}</b> negativos
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 6,
          borderRadius: 6,
          overflow: 'hidden',
          background: colors.surfaceAlt,
          border: `1px solid ${colors.softBorder}`,
          cursor: 'help',
        }}
      >
        {pos > 0 && <Box sx={{ width: `${posPct}%`, background: colors.pos.accent }} />}
        {neu > 0 && <Box sx={{ width: `${neuPct}%`, background: colors.neu.accent }} />}
        {neg > 0 && <Box sx={{ width: `${negPct}%`, background: colors.neg.accent }} />}
      </Box>
    </Tooltip>
  );
};

const MetricCard = ({
  label,
  value,
  suffix,
  sub,
  renderSub,
  icon: Icon,
  tone = 'neu',
  delay = 0,
  highlight = false,
  onClick,
  ctaLabel,
}) => {
  const { fonts, colors } = feedbackHeroTokens;
  const t = colors[tone] || colors.neu;
  const isClickable = Boolean(onClick);

  const PaperComponent = isClickable ? ButtonBase : 'div';

  return (
    <Grid item xs={12} sm={6} md={3}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay }}
        style={{ height: '100%' }}
      >
        <Paper
          elevation={0}
          component={PaperComponent}
          onClick={onClick}
          sx={{
            p: { xs: 2, sm: 2.25 },
            borderRadius: 2.5,
            background: colors.surface,
            border: highlight
              ? `1px solid ${t.accent}33`
              : `1px solid ${colors.softBorder}`,
            boxShadow: highlight
              ? `0 12px 28px ${t.accent}1A`
              : '0 10px 24px rgba(15, 23, 42, 0.05)',
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            display: 'block',
            textAlign: 'left',
            cursor: isClickable ? 'pointer' : 'default',
            transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 32px ${t.accent}24`,
              borderColor: isClickable ? `${t.accent}66` : undefined,
            },
            '&:hover .metric-cta': isClickable
              ? { transform: 'translateX(4px)', opacity: 1 }
              : {},
            '&:hover .metric-icon': isClickable
              ? { transform: 'scale(1.08)' }
              : {},
            '&::before': highlight
              ? {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(135deg, ${t.bg}33 0%, transparent 60%)`,
                pointerEvents: 'none',
              }
              : {},
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: fonts.body,
                  color: colors.subtext,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                }}
              >
                {label}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5} mt={0.5}>
                <Typography
                  sx={{
                    fontFamily: fonts.display,
                    fontWeight: 700,
                    fontSize: { xs: '1.7rem', sm: '2rem' },
                    color: colors.text,
                    lineHeight: 1.05,
                  }}
                >
                  {value}
                </Typography>
                {suffix && (
                  <Typography
                    sx={{
                      fontFamily: fonts.display,
                      fontWeight: 700,
                      fontSize: { xs: '1.2rem', sm: '1.35rem' },
                      color: t.accent,
                    }}
                  >
                    {suffix}
                  </Typography>
                )}
              </Stack>
              {renderSub ? (
                <Box sx={{ mt: 1 }}>{renderSub}</Box>
              ) : sub ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: fonts.body,
                    color: colors.subtext,
                    display: 'block',
                    mt: 0.5,
                    fontSize: '0.74rem',
                  }}
                >
                  {sub}
                </Typography>
              ) : null}
              {isClickable && ctaLabel && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  className="metric-cta"
                  sx={{
                    mt: 1,
                    transition: 'transform .25s ease, opacity .25s ease',
                    opacity: 0.85,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      color: t.fg,
                      letterSpacing: '.02em',
                    }}
                  >
                    {ctaLabel}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 12, color: t.fg }} />
                </Stack>
              )}
            </Box>
            <Box
              className="metric-icon"
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: t.bg,
                color: t.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
                border: `1px solid ${t.chip}`,
                transition: 'transform .25s ease',
              }}
            >
              {Icon ? <Icon sx={{ fontSize: 20 }} /> : null}
            </Box>
          </Stack>
        </Paper>
      </motion.div>
    </Grid>
  );
};

const FeedbackResumoCard = ({ resumo, onShowNegativos }) => {
  const { fonts, colors } = feedbackHeroTokens;

  const formatMediaNota = (value) => {
    if (value === null || value === undefined) return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return num.toFixed(1);
  };

  const getTotalBySentimento = (keys) =>
    resumo.por_sentimento?.find((s) => keys.includes(s.sentimento))?.total || 0;

  const positivos = getTotalBySentimento(['positivo', 'positivos']);
  const neutros = getTotalBySentimento(['neutro', 'neutros', 'neutral']);
  const negativos = getTotalBySentimento(['negativo', 'negativos']);
  const total = resumo.total_feedbacks ?? positivos + neutros + negativos;
  const totalAvaliados = positivos + neutros + negativos;
  const mediaNota = formatMediaNota(resumo.media_nota);
  const mediaNotaNum = Number(resumo.media_nota) || 0;
  const pendenciasAtencao = Number(resumo.pendencias_atencao ?? negativos) || 0;

  const pctPositivos = totalAvaliados > 0 ? Math.round((positivos / totalAvaliados) * 100) : 0;

  const sentimentoTone = pctPositivos >= 75 ? 'pos' : pctPositivos >= 50 ? 'neu' : 'neg';
  const semPendencias = pendenciasAtencao === 0;

  return (
    <Grid item xs={12}>
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <MetricCard
          label="Volume no período"
          value={total}
          sub={total === 1 ? 'feedback recebido' : 'feedbacks recebidos'}
          icon={ChatBubbleOutlineIcon}
          tone="neu"
          delay={0}
        />
        <MetricCard
          label="Nota média"
          value={mediaNota}
          suffix="★"
          renderSub={
            mediaNotaNum > 0 ? (
              <StarFill rating={mediaNotaNum} size={16} color={colors.neu.accent} />
            ) : (
              <Typography
                variant="caption"
                sx={{ fontFamily: fonts.body, color: colors.subtext, fontSize: '0.74rem' }}
              >
                Sem avaliações com nota
              </Typography>
            )
          }
          icon={StarBorderIcon}
          tone="neu"
          delay={0.05}
        />
        <MetricCard
          label="Sentimento positivo"
          value={`${pctPositivos}%`}
          renderSub={
            totalAvaliados > 0 ? (
              <MiniDistributionBar
                pos={positivos}
                neu={neutros}
                neg={negativos}
                colors={colors}
                fonts={fonts}
              />
            ) : (
              <Typography
                variant="caption"
                sx={{ fontFamily: fonts.body, color: colors.subtext, fontSize: '0.74rem' }}
              >
                Sem dados de sentimento
              </Typography>
            )
          }
          icon={ThumbUpAltOutlinedIcon}
          tone={sentimentoTone}
          highlight={sentimentoTone === 'pos'}
          delay={0.1}
        />
        <MetricCard
          label={semPendencias ? 'Tudo em ordem' : 'Precisam de atenção'}
          value={pendenciasAtencao}
          sub={
            semPendencias
              ? 'Nenhuma pendência negativa aberta'
              : `${pendenciasAtencao === 1 ? 'avaliação negativa pendente' : 'avaliações negativas pendentes'} no período`
          }
          icon={semPendencias ? VerifiedOutlinedIcon : ReportProblemOutlinedIcon}
          tone={semPendencias ? 'pos' : 'neg'}
          highlight={!semPendencias}
          onClick={!semPendencias && onShowNegativos ? onShowNegativos : undefined}
          ctaLabel={!semPendencias ? 'Ver pendências' : undefined}
          delay={0.15}
        />
      </Grid>
    </Grid>
  );
};

export default FeedbackResumoCard;
